import axios from 'axios';
import {useCallback, useEffect, useState} from 'react';

import {createEndpointHook} from './util/endpointhooks.ts';

/**
 * Loads a user by id.
 *
 * @param {string} userId
 * @return {Promise<any>}
 */
function loadUserDetail(userId) {
    return axios.get(`/api/user-access/users/${userId}`).then((response) => response.data);
}

/**
 * Updates the user information, such as name, last name.
 *
 * @param {string} userId
 * @param {object} data
 *
 * @return {Promise<any>}
 */
export function updateUserInformation(userId, data) {
    return axios.post(`/api/user-access/users/${userId}`, data);
}

/**
 * Sends a request to the backend to update the user status.
 * We use this to block/unblock/approve a user.
 *
 * @param {string} userId
 * @param {{status: string}} data
 *
 * @return {Promise<any>}
 */
export function updateUserStatus(userId, data) {
    return axios.post(`/api/user-access/users/${userId}/status`, data);
}

export function addApplicationRoles(userId, data) {
    return axios.post(`/api/user-access/users/${userId}/roles`, data);
}

export function editProfileData(userId, applicationRoleId, data) {
    return axios.post(`/api/user-access/users/${userId}/roles/${applicationRoleId}`, data);
}

export function removeApplicationRole(userId, applicationRoleId) {
    return axios.delete(`/api/user-access/users/${userId}/roles/${applicationRoleId}`);
}

export function deleteUser(userId) {
    return axios.delete(`/api/user-access/users/${userId}`);
}

export function createUser(data) {
    return axios.post(`/api/user-access/users`, data);
}

export function createUserWithoutProfiles(data) {
    return axios.post(`/api/user-access/users-empty`, data);
}

/**
 * Given the user id, it retrieves the registration state.
 * The state contains information about the current status of the user's
 * registration, what the current user is allowed to do for the user, and
 * what the user themselves can do.
 *
 * @param {string} userId
 *
 * @return {Promise<any>}
 */
export async function getUserRegistrationState(userId) {
    return axios.get(`/api/user-access/users/${userId}/registration-state`).then((response) => response.data);
}

function useUserDetail(userId) {
    const [userDetail, setUserDetail] = useState(null);

    const reload = useCallback(() => {
        loadUserDetail(userId)
            .then((opts) => setUserDetail(opts))
            .catch((e) => {
                setUserDetail(null);
                console.error(e);
            });
    }, [userId]);

    useEffect(() => reload(), [reload]);

    return {
        userDetail,
        reload,
    };
}

/**
 * Loads a user by id.
 */
export const useUserDetailExtended = createEndpointHook(loadUserDetail);

/**
 * Loads a user by id.
 */
export const useUserRegistrationState = createEndpointHook(getUserRegistrationState);

export default useUserDetail;
